import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Briefcase, Users, Activity, TrendingUp, Calendar, MessageSquare, AlertCircle, CheckCircle, Search, X } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import { PractitionerDetailModal } from './PractitionerDetailModal';
import { useLoadingState } from '@/hooks/useLoadingState';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';

interface PractitionerData {
  id: string;
  name: string;
  email: string;
  clientCount: number;
  activeClients: number;
  totalSessions: number;
  recentSessions: number;
  joinDate: string;
  lastActive: string;
  status: 'active' | 'inactive' | 'needs-support';
}

interface PractitionerMetrics {
  totalPractitioners: number;
  activePractitioners: number;
  totalClients: number;
  averageClientsPerPractitioner: number;
  totalSessionsCreated: number;
  practitionersNeedingSupport: number;
}

const PractitionerManagement: React.FC = () => {
  const [practitioners, setPractitioners] = useState<PractitionerData[]>([]);
  const [metrics, setMetrics] = useState<PractitionerMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPractitioner, setSelectedPractitioner] = useState<PractitionerData | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const { isLoading, withLoading } = useLoadingState();

  useEffect(() => {
    fetchPractitionerData();
  }, []);

  const fetchPractitionerData = async () => {
    try {
      setLoading(true);
      console.log('🚀 Starting practitioner data fetch...');

      // Fetch practitioners with their roles - include both 'practitioner' and 'partners' roles
      const { data: practitionerRoles } = await backendClient
        .from('user_roles')
        .select('user_id')
        .in('role', ['practitioner', 'partners']);

      console.log('🔍 Practitioner roles found:', practitionerRoles);

      if (!practitionerRoles?.length) {
        console.log('❌ No practitioner roles found');
        setMetrics({
          totalPractitioners: 0,
          activePractitioners: 0,
          totalClients: 0,
          averageClientsPerPractitioner: 0,
          totalSessionsCreated: 0,
          practitionersNeedingSupport: 0
        });
        setPractitioners([]);
        setLoading(false);
        return;
      }

      // Deduplicate user IDs in case a user has both practitioner and partners roles
      const practitionerIds = [...new Set(practitionerRoles.map(r => r.user_id))];
      console.log('🔍 Practitioner IDs (deduplicated):', practitionerIds);

      // Fetch practitioner profiles - include all practitioners, handle incomplete data gracefully
      const { data: profiles, error: profilesError } = await backendClient
        .from('profiles')
        .select('*')
        .in('id', practitionerIds);

      console.log('🔍 All profiles found:', profiles);
      console.log('🔍 Profile query error (if any):', profilesError);
      
      if (profilesError) {
        console.error('❌ Profile query failed:', profilesError);
      }
      
      // Log each practitioner ID and whether we found their profile
      practitionerIds.forEach(id => {
        const foundProfile = profiles?.find(p => p.id === id);
        console.log(`🔍 Profile lookup for ${id}:`, foundProfile ? 'FOUND' : 'NOT FOUND', foundProfile);
      });
      
      // Create placeholder profiles for practitioners without profile data
      const existingProfileIds = new Set(profiles?.map(p => p.id) || []);
      const missingProfileIds = practitionerIds.filter(id => !existingProfileIds.has(id));
      
      console.log('🔍 Missing profile IDs:', missingProfileIds);
      
      // Create complete profiles array with placeholders
      const allProfiles = [
        ...(profiles || []),
        ...missingProfileIds.map(id => ({
          id,
          email: null,
          full_name: null,
          first_name: null,
          last_name: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }))
      ];
      
      console.log('🔍 Complete profiles array:', allProfiles);
      
      // Filter out profiles with critical missing data, but be more lenient
      const validProfiles = allProfiles.filter(profile => {
        const hasBasicInfo = profile.id; // Only require ID since we create placeholders
        if (!hasBasicInfo) {
          console.log('⚠️ Excluding profile due to missing ID:', profile);
        }
        return hasBasicInfo;
      });

      console.log('🔍 Valid profiles after filtering:', validProfiles);

      if (!validProfiles.length) {
        console.log('❌ No valid profiles found after filtering');
        setMetrics({
          totalPractitioners: 0,
          activePractitioners: 0,
          totalClients: 0,
          averageClientsPerPractitioner: 0,
          totalSessionsCreated: 0,
          practitionersNeedingSupport: 0
        });
        setPractitioners([]);
        setLoading(false);
        return;
      }

      // Process practitioners with valid profiles
      const validPractitionerIds = validProfiles.map(p => p.id);
      console.log('🔍 Valid practitioner IDs for data fetching:', validPractitionerIds);

      // Fetch clients for valid practitioners only
      const { data: clients } = await backendClient
        .from('practitioner_clients')
        .select('*')
        .in('practitioner_id', validPractitionerIds)
        .eq('is_active', true);

      console.log('🔍 Clients found:', clients);

      // Fetch sessions created by valid practitioners
      const { data: sessions } = await backendClient
        .from('sessions')
        .select('*')
        .in('practitioner_id', validPractitionerIds);

      console.log('🔍 Sessions found:', sessions);

      // Process practitioner data - only valid practitioners with profiles
      const processedPractitioners: PractitionerData[] = validProfiles.map(profile => {
        const practitionerClients = clients?.filter(c => c.practitioner_id === profile.id) || [];
        const practitionerSessions = sessions?.filter(s => s.practitioner_id === profile.id) || [];
        
        console.log(`📊 Data for practitioner ${profile.id}:`, {
          clientsFound: practitionerClients.length,
          sessionsFound: practitionerSessions.length
        });
        
        // Calculate recent sessions (last 7 days)
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const recentSessions = practitionerSessions.filter(s => 
          new Date(s.created_at) > weekAgo
        ).length;

        // Determine status
        let status: 'active' | 'inactive' | 'needs-support' = 'active';
        if (practitionerClients.length === 0) {
          status = 'needs-support';
        } else if (recentSessions === 0 && practitionerSessions.length < 5) {
          status = 'inactive';
        }

        // Find last session date
        const lastSession = practitionerSessions
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

        // Construct name with better fallback logic
        let displayName = profile.full_name;
        if (!displayName) {
          const firstPart = profile.first_name || '';
          const lastPart = profile.last_name || '';
          displayName = `${firstPart} ${lastPart}`.trim();
        }
        if (!displayName && profile.email) {
          // Use email username as fallback
          displayName = profile.email.split('@')[0];
        }
        if (!displayName) {
          displayName = `Practitioner ${profile.id.slice(0, 8)}`;
        }

        const practitionerData = {
          id: profile.id,
          name: displayName,
          email: profile.email || 'No email on file',
          clientCount: practitionerClients.length,
          activeClients: practitionerClients.filter(c => c.is_active).length,
          totalSessions: practitionerSessions.length,
          recentSessions,
          joinDate: profile.created_at || new Date().toISOString(),
          lastActive: lastSession?.created_at || profile.created_at || new Date().toISOString(),
          status
        };
        
        console.log(`📊 Final processed data for ${practitionerData.name}:`, {
          clients: practitionerData.clientCount,
          sessions: practitionerData.totalSessions,
          status: practitionerData.status
        });
        
        return practitionerData;
      });

      console.log('✅ Processed practitioner data:', processedPractitioners);

      // Calculate metrics with unique client counting to avoid duplicates
      const uniqueClients = new Set();
      clients?.forEach(client => {
        uniqueClients.add(`${client.client_name}-${client.email || 'no-email'}`);
      });
      const totalClients = uniqueClients.size;
      
      const totalSessions = sessions?.length || 0;
      const activePractitioners = processedPractitioners.filter(p => p.status === 'active').length;
      const practitionersNeedingSupport = processedPractitioners.filter(p => p.status === 'needs-support').length;

      setMetrics({
        totalPractitioners: processedPractitioners.length,
        activePractitioners,
        totalClients,
        averageClientsPerPractitioner: processedPractitioners.length > 0 
          ? Math.round(totalClients / processedPractitioners.length * 10) / 10 
          : 0,
        totalSessionsCreated: totalSessions,
        practitionersNeedingSupport
      });

      setPractitioners(processedPractitioners.sort((a, b) => b.totalSessions - a.totalSessions));
      
      console.log('✅ Final metrics:', {
        totalPractitioners: processedPractitioners.length,
        activePractitioners,
        totalClients,
        averageClientsPerPractitioner: processedPractitioners.length > 0 ? Math.round(totalClients / processedPractitioners.length * 10) / 10 : 0,
        totalSessionsCreated: totalSessions,
        practitionersNeedingSupport
      });

    } catch (error) {
      console.error('Error fetching practitioner data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load practitioner data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20 hover:bg-slate-500/20">Inactive</Badge>;
      case 'needs-support':
        return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20">Needs Support</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const sendSupportMessage = async (practitionerId: string) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    if (practitioner) {
      setSelectedPractitioner(practitioner);
      setDetailModalOpen(true);
      // Auto-focus on support tab by setting a small delay
      setTimeout(() => {
        const supportTabTrigger = document.querySelector('[data-state="inactive"][value="support"]') as HTMLElement;
        if (supportTabTrigger) {
          supportTabTrigger.click();
        }
      }, 100);
    }
  };

  const handleViewDetails = (practitioner: PractitionerData) => {
    setSelectedPractitioner(practitioner);
    setDetailModalOpen(true);
  };

  // Filter practitioners based on search query
  const filteredPractitioners = practitioners.filter(practitioner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      practitioner.name.toLowerCase().includes(query) ||
      practitioner.email.toLowerCase().includes(query) ||
      practitioner.status.toLowerCase().includes(query)
    );
  });

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<PractitionerData>(
    filteredPractitioners,
    'totalSessions',
    'desc'
  );

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Practitioner Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Practitioners</p>
                <p className="text-3xl font-bold text-blue-900">{metrics?.totalPractitioners || 0}</p>
                <p className="text-xs text-blue-600 mt-1">{metrics?.activePractitioners || 0} active</p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Clients</p>
                <p className="text-3xl font-bold text-green-900">{metrics?.totalClients || 0}</p>
                <p className="text-xs text-green-600 mt-1">
                  {metrics?.averageClientsPerPractitioner || 0} avg per practitioner
                </p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Sessions Created</p>
                <p className="text-3xl font-bold text-purple-900">{metrics?.totalSessionsCreated || 0}</p>
                <p className="text-xs text-purple-600 mt-1">By all practitioners</p>
              </div>
              <Activity className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Support Alerts */}
      {metrics && metrics.practitionersNeedingSupport > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-orange-500" />
              <div>
                <p className="font-medium text-orange-900">
                  {metrics.practitionersNeedingSupport} practitioner{metrics.practitionersNeedingSupport > 1 ? 's' : ''} may need support
                </p>
                <p className="text-sm text-orange-700">
                  These practitioners have no clients or very low activity. Consider reaching out.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Practitioner Directory */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Practitioner Directory
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Manage and monitor all practitioners on the platform.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPractitionerData}
              disabled={loading}
            >
              Refresh Data
            </Button>
          </div>

          {/* Search Bar */}
          {practitioners.length > 0 && (
            <div className="flex items-center gap-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {practitioners.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No practitioners found</p>
              <p className="text-sm text-muted-foreground">
                Practitioners will appear here once they register and are assigned the practitioner role.
              </p>
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No practitioners found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search query
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    onSort={() => requestSort('name')}
                    sortDirection={getSortIndicator('name')}
                  >
                    Practitioner
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('status')}
                    sortDirection={getSortIndicator('status')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('clientCount')}
                    sortDirection={getSortIndicator('clientCount')}
                  >
                    Clients
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('totalSessions')}
                    sortDirection={getSortIndicator('totalSessions')}
                  >
                    Sessions
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('lastActive')}
                    sortDirection={getSortIndicator('lastActive')}
                  >
                    Recent Activity
                  </SortableTableHead>
                  <SortableTableHead sortable={false}>
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((practitioner) => (
                  <TableRow key={practitioner.id}>
                     <TableCell>
                       <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
                           {practitioner.name.charAt(0).toUpperCase()}
                         </div>
                         <div>
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium">{practitioner.name}</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                                    Practitioner
                                  </Badge>
                                  {(practitioner.email === 'No email on file' || practitioner.name === 'Unnamed Practitioner' || practitioner.name.includes('@')) && (
                                    <div title="Incomplete profile - missing name or contact information" className="flex items-center">
                                      <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                           <p className="text-sm text-muted-foreground mt-1">{practitioner.email}</p>
                           <p className="text-xs text-muted-foreground">
                             Joined {new Date(practitioner.joinDate).toLocaleDateString()}
                           </p>
                         </div>
                       </div>
                     </TableCell>
                    <TableCell>
                      {getStatusBadge(practitioner.status)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{practitioner.activeClients}</p>
                        <p className="text-xs text-muted-foreground">
                          {practitioner.clientCount} total
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{practitioner.totalSessions}</p>
                        <p className="text-xs text-muted-foreground">
                          {practitioner.recentSessions} this week
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">
                        {new Date(practitioner.lastActive).toLocaleDateString()}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {practitioner.status === 'needs-support' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendSupportMessage(practitioner.id)}
                            className="text-xs"
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Support
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs"
                          onClick={() => handleViewDetails(practitioner)}
                        >
                          View Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Performing Practitioners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {practitioners.slice(0, 5).map((practitioner, index) => (
                <div key={practitioner.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">
                      #{index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{practitioner.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {practitioner.activeClients} clients
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{practitioner.totalSessions}</p>
                    <p className="text-xs text-muted-foreground">sessions</p>
                  </div>
                </div>
              ))}
              {practitioners.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No data available yet
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Recent Activity Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium">Active This Week</p>
                    <p className="text-sm text-muted-foreground">Created sessions recently</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-green-700">
                  {practitioners.filter(p => p.recentSessions > 0).length}
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Low Activity</p>
                    <p className="text-sm text-muted-foreground">No sessions this week</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-yellow-700">
                  {practitioners.filter(p => p.recentSessions === 0 && p.totalSessions > 0).length}
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-medium">Need Support</p>
                    <p className="text-sm text-muted-foreground">No clients or very low activity</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-red-700">
                  {practitioners.filter(p => p.status === 'needs-support').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <PractitionerDetailModal
        practitioner={selectedPractitioner}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedPractitioner(null);
        }}
      />
    </div>
  );
};

export { PractitionerDetailModal };
export default PractitionerManagement;
